import React from "react";
import {
  FileCheck,
  User,
  GraduationCap,
  Award,
  Briefcase,
  Presentation,
  Users,
  ArrowLeft,
  CheckCircle,
  Clock,
  ShieldAlert,
  ClipboardCheck,
  Fingerprint,
  Mail,
  Phone,
} from "lucide-react";
import { STATUS_BLURBS } from "../../utils/applicationStatus";

function Row({ k, v, fullWidth = false }: { k: string; v: any; fullWidth?: boolean }) {
  const val =
    v === null || v === undefined || v === ""
      ? "—"
      : typeof v === "boolean"
      ? v ? "Yes" : "No"
      : String(v);

  return (
    <div className={`flex ${fullWidth ? "flex-col gap-1" : "justify-between gap-4"} border-b border-gray-50 py-3 text-sm last:border-0`}>
      <div className="text-gray-400 font-medium uppercase text-[10px] tracking-wider shrink-0">{k}</div>
      <div className={`text-gray-900 font-semibold ${fullWidth ? "text-left" : "text-right"} break-words`}>{val}</div>
    </div>
  );
}

function Section({ title, icon: Icon, count, children }: { title: string; icon: any; count?: number; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-[24px] overflow-hidden shadow-sm">
      <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight">{title}</h3>
        </div>
        {count !== undefined && (
          <span className="bg-gray-900 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{count}</span>
        )}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="text-xs text-gray-400 italic">{text}</p>;
}

function refereeName(member: any): string {
  if (!member) return "—";
  return [member.title, member.surname, member.firstname ?? member.other_names ?? member.othernames]
    .filter(Boolean)
    .join(" ");
}

export default function Step8Acknowledge({
  application,
  onSubmit,
  busy,
  onBack,
  /**
   * What pressing the button actually does.
   *
   * On a probationer application it submits. On an UPGRADE it only
   * acknowledges — payment is what submits — so the default wording promised
   * something that had not happened and left the applicant waiting on a review
   * nobody had been asked for.
   */
  submitLabel = "Confirm & Submit Application",
  busyLabel = "Submitting…",
}: {
  application: any | null;
  busy: boolean;
  onBack: () => void;
  onSubmit: () => Promise<void>;
  submitLabel?: string;
  busyLabel?: string;
}) {
  const a = application;

  if (!a) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <Clock className="w-10 h-10 text-gray-200 animate-pulse" />
        <div className="text-sm font-medium text-gray-400">Loading your dossier…</div>
        <button onClick={onBack} className="text-xs font-bold underline">Go Back</button>
      </div>
    );
  }

  const qualifications = Array.isArray(a.qualifications) ? a.qualifications : [];
  const memberships    = Array.isArray(a.professional_memberships) ? a.professional_memberships : [];
  const work           = Array.isArray(a.work_experiences) ? a.work_experiences : [];
  const seminars       = Array.isArray(a.seminars) ? a.seminars : [];
  const referees       = Array.isArray(a.referees) ? a.referees : [];
  const olevels        = Array.isArray(a.o_level_results) ? a.o_level_results : [];

  return (
    <div className="space-y-8 pb-10">
      {/* Header */}
      <div className="border-b border-gray-100 pb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-600 rounded-xl">
            <FileCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Review & Submit</h2>
            <p className="text-sm text-gray-500">
              Please cross-check all information before final submission.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">

        {/* ── Personal Details ─────────────────────────────── */}
        <Section title="Personal Information" icon={User}>
          <div className="grid md:grid-cols-2 gap-x-8">
            <Row k="Full Name" v={[a.title, a.surname, a.other_names].filter(Boolean).join(" ")} />
            <Row k="Gender" v={a.gender} />
            <Row k="Email Address" v={a.email} />
            <Row k="Phone Number" v={a.phone} />
            <Row k="Date of Birth" v={a.date_of_birth} />
            <Row k="Nationality" v={a.nationality} />
            <Row k="State of Origin" v={a.state?.name} />
            <Row k="L.G.A." v={a.lga?.name ?? a.local_government?.name} />
            <Row k="Chapter" v={a.chapter?.name ?? a.chapter_other} />
            <Row k="Professional Sector" v={a.sector?.name} />
          </div>
          <div className="mt-2 grid md:grid-cols-2 gap-x-8">
            <Row k="Residential Address" v={a.residential_address} fullWidth />
            <Row k="Postal Address" v={a.postal_address} fullWidth />
          </div>
        </Section>

        {/* ── Academic Qualifications ──────────────────────── */}
        <Section title="Academic History" icon={GraduationCap} count={qualifications.length}>
          {qualifications.length === 0 ? (
            <Empty text="No academic records provided." />
          ) : (
            <div className="space-y-4">
              {qualifications.map((q: any, i: number) => (
                <div key={i} className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <Row k="Institution" v={q.institution} />
                  <Row k="Qualification" v={q.qualification} />
                  <Row k="Year" v={q.year} />
                  {(q.cert_file || q.cert_url || q.certificate_url) && (
                    <Row k="Certificate" v="On File" />
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── O-Level Results ──────────────────────────────── */}
        <Section title="O-Level Results" icon={ClipboardCheck} count={olevels.length}>
          {olevels.length === 0 ? (
            <Empty text="No O-Level results provided." />
          ) : (
            <div className="space-y-6">
              {olevels.map((r: any, i: number) => {
                const subjects: Record<string, string> = r.subjects || {};
                return (
                  <div key={i} className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                    <div className="flex flex-wrap gap-4 mb-4 pb-3 border-b border-gray-100">
                      <div>
                        <div className="text-[10px] font-bold text-gray-400 uppercase">Exam Type</div>
                        <div className="font-bold text-gray-900 uppercase text-sm">{r.exam_type}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold text-gray-400 uppercase">Year</div>
                        <div className="font-bold text-gray-900 text-sm">{r.exam_year}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold text-gray-400 uppercase">Exam No.</div>
                        <div className="font-bold text-gray-900 text-sm">{r.exam_number || "—"}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {Object.entries(subjects).map(([subject, grade]) => (
                        <div key={subject} className="flex items-center justify-between bg-white border border-gray-100 rounded-xl px-3 py-2 text-xs">
                          <span className="text-gray-600 font-medium capitalize">{subject}</span>
                          <span className="font-black text-gray-900">{grade}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* ── Work Experience ──────────────────────────────── */}
        <Section title="Work Experience" icon={Briefcase} count={work.length}>
          {work.length === 0 ? (
            <Empty text="No work experience provided." />
          ) : (
            <div className="space-y-4">
              {work.map((w: any, i: number) => (
                <div key={i} className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <Row k="Organization" v={w.organization} />
                  <Row k="Position" v={w.position} />
                  <Row k="Duration" v={`${w.start_date} — ${w.end_date || "Present"}`} />
                  {w.responsibilities && <Row k="Responsibilities" v={w.responsibilities} fullWidth />}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── Memberships & Seminars side by side ─────────── */}
        <div className="grid md:grid-cols-2 gap-6">
          <Section title="Professional Memberships" icon={Award} count={memberships.length}>
            {memberships.length === 0 ? (
              <Empty text="None provided." />
            ) : (
              <div className="space-y-4">
                {memberships.map((m: any, i: number) => (
                  <div key={i} className="border-b border-gray-100 last:border-0 pb-3">
                    <div className="font-bold text-gray-900">{m.body_name}</div>
                    {m.stage_passed && <div className="text-xs text-gray-500 mt-0.5">{m.stage_passed}</div>}
                    {m.certificate_path && (
                      <div className="text-[10px] text-gray-400 mt-1 truncate">{m.certificate_path}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Seminars & Workshops" icon={Presentation} count={seminars.length}>
            {seminars.length === 0 ? (
              <Empty text="None provided." />
            ) : (
              <div className="space-y-4">
                {seminars.map((s: any, i: number) => (
                  <div key={i} className="border-b border-gray-100 last:border-0 pb-3">
                    <div className="font-bold text-gray-900 text-sm">{s.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{s.date}{s.location ? ` · ${s.location}` : ""}</div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* ── Referees ─────────────────────────────────────── */}
        <Section title="Referees" icon={Users} count={referees.length}>
          {referees.length === 0 ? (
            <Empty text="No referees selected." />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {referees.map((r: any, i: number) => {
                const m = r.member;
                return (
                  <div key={i} className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white font-bold text-base shrink-0">
                        {m?.surname?.[0] ?? "?"}
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 leading-tight">{refereeName(m)}</div>
                        {m?.membership_no && (
                          <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
                            <Fingerprint className="w-3 h-3" /> {m.membership_no}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1 border-t border-gray-100 pt-3">
                      {m?.email && (
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <Mail className="w-3 h-3 shrink-0" /> {m.email}
                        </div>
                      )}
                      {(m?.phone || m?.phone2) && (
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <Phone className="w-3 h-3 shrink-0" /> {m.phone || m.phone2}
                        </div>
                      )}
                      <div className="text-[10px] font-bold text-gray-400 uppercase pt-1">
                        Relationship: <span className="text-gray-700 normal-case font-semibold">{r.relationship}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>

      {/* ── Declaration & Submit ─────────────────────────── */}
      <div className="p-6 rounded-[32px] bg-gray-900 text-white shadow-xl space-y-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10">
          <ShieldAlert className="w-24 h-24" />
        </div>

        <div className="relative z-10">
          <h4 className="text-lg font-bold mb-2 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            Declaration
          </h4>
          <p className="text-gray-400 text-sm leading-relaxed">
            I hereby certify that the information provided in this application
            is true and complete to the best of my knowledge. I understand that
            any false statement may be grounds for disqualification.
          </p>
        </div>

        {/* Still the applicant's, or gone? Listed the RIGHT way round — the
            states they still hold — because this screen is shared by two
            application types and "anything I don't recognise has been
            submitted" was wrong for one of them.

            A probationer application starts at `pending`. An UPGRADE starts at
            `draft` and sits at `pending_payment` while the fee is outstanding,
            and neither was in the old list: the upgrade wizard therefore told
            every applicant "Application Submitted — under review" over a draft,
            and hid the button that acknowledges it. Nothing was submitted and
            nothing could be paid, because payment refuses an application that
            has not been acknowledged — so the member reported having applied
            while the secretariat saw a draft and no money. */}
        {!["pending", "draft", "pending_payment", "returned"].includes(String(a.status)) ? (
          <div className="flex items-center gap-3 px-5 py-4 bg-green-500/10 border border-green-500/20 rounded-2xl relative z-10">
            <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
            <div>
              <div className="font-bold text-green-400 text-sm">Application Submitted</div>
              <div className="text-green-400/70 text-xs mt-0.5">
                {STATUS_BLURBS[String(a.status)] ??
                  "Your application has been submitted and is under review."}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t border-white/10 relative z-10">
            <button
              type="button"
              disabled={busy}
              onClick={onBack}
              className="flex items-center gap-2 px-6 py-3 text-white/60 hover:text-white transition-all font-bold text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Wait, I need to edit
            </button>

            <button
              disabled={busy}
              onClick={onSubmit}
              className="w-full md:w-auto flex items-center justify-center gap-3 px-10 py-4 bg-green-500 hover:bg-green-400 text-gray-900 rounded-2xl font-black text-sm transition-all shadow-lg shadow-green-500/20 disabled:opacity-50"
            >
              {busy ? (
                <>
                  <Clock className="w-4 h-4 animate-spin" />
                  {busyLabel}
                </>
              ) : (
                submitLabel
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
